<app-oee-modal
    title="Add Time Entry"
    [subtitle]="dateDisplay"
    icon="fa-plus"
    iconColor="green"
    tint="green"
    size="lg"
    [loading]="loading"
    loadingMessage="Loading..."
    (closeClick)="onCancel()">

    <ng-container modal-content>
        @if (error) {
            <div class="alert alert-danger mb-3">{{ error }}</div>
        }

        <div class="add-shift-form">
            @if (duplicateTemplates.length > 0) {
                <div class="field-block">
                    <div class="field-header-row">
                        <label class="field-label mb-0">Quick Copy From Another Day</label>
                        <span class="text-muted small">Copies the selected entry onto {{ dateDisplay }}</span>
                    </div>
                    <div class="duplicate-template-list">
                        @for (template of duplicateTemplates; track template.id) {
                            <button
                                type="button"
                                class="duplicate-template-btn"
                                [class.active]="selectedDuplicateTemplateId === template.id"
                                (click)="applyDuplicateTemplate(template)">
                                <div class="duplicate-template-title">{{ template.title }}</div>
                                <div class="duplicate-template-subtitle">{{ template.sourceDateLabel }} · {{ template.subtitle }}</div>
                            </button>
                        }
                    </div>
                </div>
            }

            <div class="toggle-row mb-3">
                <button type="button" class="toggle-btn" [class.active]="mode === 'job'" (click)="setMode('job')">
                    <i class="fa-solid fa-industry me-1"></i> Job
                </button>
                <button type="button" class="toggle-btn" [class.active]="mode === 'activity'" (click)="setMode('activity')">
                    <i class="fa-solid fa-tag me-1"></i> Activity
                </button>
            </div>

            @if (mode === 'job') {
                <div class="job-layout">
                    <div class="field-block">
                        <label class="field-label">Station *</label>
                        <select class="form-select" [(ngModel)]="selectedStationId" (change)="onStationChange()">
                            <option [ngValue]="null">Select a station</option>
                            @for (station of stations; track station.id) {
                                <option [ngValue]="station.id">{{ station.station_code }} - {{ station.station_name }}</option>
                            }
                        </select>
                    </div>

                    <div class="date-grid">
                        <app-datetime-picker
                            label="Start"
                            mode="datetime"
                            [required]="true"
                            [initialDate]="startPickerInitialDate"
                            [(ngModel)]="startDateObj">
                        </app-datetime-picker>

                        <app-datetime-picker
                            label="End"
                            mode="datetime"
                            [required]="isMultipleJobMode"
                            [hint]="isMultipleJobMode ? 'Required when logging multiple jobs' : 'Leave blank to start an active entry'"
                            [initialDate]="endPickerInitialDate"
                            [referenceTime]="startDateObj"
                            [(ngModel)]="endDateObj">
                        </app-datetime-picker>
                    </div>

                    <div class="field-block">
                        <label class="field-label">Search Jobs</label>
                        <input
                            type="text"
                            class="form-control"
                            placeholder="Search by batch number, work order, or station"
                            [(ngModel)]="searchQuery" />
                    </div>

                    <div class="field-block">
                        <div class="field-header-row">
                            <label class="field-label mb-0">Available Jobs</label>
                            <button
                                type="button"
                                class="mode-toggle-btn"
                                [class.multiple]="jobEntryMode === 'multiple'"
                                (click)="setJobEntryMode(jobEntryMode === 'single' ? 'multiple' : 'single')">
                                <span class="mode-toggle-thumb"></span>
                                <span class="mode-toggle-option" [class.active]="jobEntryMode === 'single'">
                                    <i class="fa-solid fa-circle-dot"></i>
                                    One Job
                                </span>
                                <span class="mode-toggle-option" [class.active]="jobEntryMode === 'multiple'">
                                    <i class="fa-solid fa-list-check"></i>
                                    Multiple Jobs
                                </span>
                            </button>
                        </div>
                        <div class="batch-results">
                            @for (batch of filteredBatches; track batch.batch_event_id) {
                                <label class="batch-option" [class.selected]="isBatchSelected(batch)" [class.single-select]="jobEntryMode === 'single'">
                                    <input
                                        [type]="jobEntryMode === 'single' ? 'radio' : 'checkbox'"
                                        [name]="jobEntryMode === 'single' ? 'selected-rma' : null"
                                        [checked]="isBatchSelected(batch)"
                                        (change)="toggleBatchSelection(batch)" />
                                    <div class="batch-copy">
                                        <div class="batch-title">
                                            {{ batch.batch_number }}
                                            @if (getRecentlyClosedPill(batch); as pill) {
                                                <span class="batch-recently-closed-pill"
                                                      [title]="pill.tooltip"
                                                      data-testid="add-shift-recently-closed-pill">
                                                    {{ pill.label }}
                                                </span>
                                            }
                                        </div>
                                        <div class="batch-subtitle">{{ getBatchSubtitle(batch) }}</div>
                                    </div>
                                </label>
                            } @empty {
                                <div class="empty-results">No jobs match this search.</div>
                            }
                        </div>

                        @if (jobEntryMode === 'single') {
                            <div class="time-summary">
                                Select one job to start or log time against.
                            </div>
                        }
                    </div>

                    @if (selectedBatches.length > 0 && jobEntryMode === 'multiple') {
                        <div class="field-block">
                            <label class="field-label">
                                Selected Jobs ({{ selectedBatches.length }})
                                @if (jobEntryMode === 'multiple' && selectedBatches.length > 1) {
                                    <span class="text-muted"> · One entry, shared across all jobs</span>
                                }
                            </label>

                            <div class="selected-batches">
                                @for (batch of selectedBatches; track batch.batch_event_id; let i = $index) {
                                    <div class="selected-batch-row">
                                        <div class="selected-batch-copy">
                                            <div class="selected-batch-title">{{ i + 1 }}. {{ batch.batch_number }}</div>
                                            <div class="selected-batch-subtitle">{{ getBatchSubtitle(batch) }}</div>
                                        </div>

                                        <div class="selected-batch-actions">
                                            <button type="button" class="icon-btn" title="Move up" [disabled]="i === 0" (click)="moveSelectedBatch(i, -1)">
                                                <i class="fa-solid fa-chevron-up"></i>
                                            </button>
                                            <button type="button" class="icon-btn" title="Move down" [disabled]="i === selectedBatches.length - 1" (click)="moveSelectedBatch(i, 1)">
                                                <i class="fa-solid fa-chevron-down"></i>
                                            </button>
                                            <button type="button" class="icon-btn icon-btn-danger" title="Remove" (click)="removeSelectedBatch(i)">
                                                <i class="fa-solid fa-xmark"></i>
                                            </button>
                                        </div>
                                    </div>
                                }
                            </div>

                            @if (totalMinutes) {
                                <div class="time-summary">
                                    Total time: {{ totalMinutes }} minute{{ totalMinutes === 1 ? '' : 's' }}
                                    @if (jobEntryMode === 'multiple' && selectedBatches.length > 1) {
                                        <span> · Split across {{ selectedBatches.length }} jobs</span>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            }

            @if (mode === 'activity') {
                <div class="activity-layout">
                    <div class="activity-badges-row mb-3">
                        @for (cat of activityCategories; track cat.name) {
                            <button class="activity-badge-btn"
                                [class.active]="selectedCategory === cat.name"
                                (click)="selectCategory(cat.name)">
                                <i [class]="cat.icon"></i> {{ cat.name }}
                            </button>
                        }
                    </div>

                    @if (isWaitingOnPartsSelected) {
                        <div class="field-block waiting-on-parts-picker">
                            <label class="field-label">Which job(s) are you waiting on? <span class="text-muted">(optional)</span></label>

                            <select class="form-select form-select-sm mb-2" [(ngModel)]="waitingOnPartsStationId">
                                <option [ngValue]="null">All stations</option>
                                @for (station of stations; track station.id) {
                                    <option [ngValue]="station.id">{{ station.station_code }} - {{ station.station_name }}</option>
                                }
                            </select>
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Search by batch number, work order, equipment, or product"
                                [(ngModel)]="waitingOnPartsSearchQuery" />
                            <div class="batch-results batch-results-compact">
                                @for (batch of filteredWaitingOnPartsBatches; track batch.batch_event_id) {
                                    <label class="batch-option"
                                        [class.selected]="isWaitingBatchSelected(batch)">
                                        <input
                                            type="checkbox"
                                            [checked]="isWaitingBatchSelected(batch)"
                                            (change)="toggleWaitingOnPartsBatch(batch)" />
                                        <div class="batch-copy">
                                            <div class="batch-title">{{ batch.batch_number }}</div>
                                            <div class="batch-subtitle">{{ getBatchSubtitle(batch) }}</div>
                                        </div>
                                    </label>
                                } @empty {
                                    <div class="empty-results">No jobs match this search.</div>
                                }
                            </div>

                            @if (waitingOnPartsBatches.length > 0) {
                                <div class="selected-waiting-batches mt-2">
                                    @for (batch of waitingOnPartsBatches; track batch.batch_event_id) {
                                        <div class="selected-waiting-batch-row">
                                            <div class="selected-waiting-batch-copy">
                                                <span class="batch-title">{{ batch.batch_number }}</span>
                                                <span class="batch-subtitle">{{ getBatchSubtitle(batch) }}</span>
                                            </div>
                                            <button type="button" class="btn-remove-batch" (click)="toggleWaitingOnPartsBatch(batch)" title="Remove">
                                                <i class="fa-solid fa-xmark"></i>
                                            </button>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }

                    <div class="date-grid">
                        <app-datetime-picker
                            label="Start"
                            mode="datetime"
                            [required]="true"
                            [initialDate]="startPickerInitialDate"
                            [(ngModel)]="startDateObj">
                        </app-datetime-picker>

                        <app-datetime-picker
                            label="End"
                            mode="datetime"
                            hint="Leave blank for active shift"
                            [initialDate]="endPickerInitialDate"
                            [referenceTime]="startDateObj"
                            [(ngModel)]="endDateObj">
                        </app-datetime-picker>
                    </div>
                </div>
            }

            <!-- Notes (always visible regardless of mode) -->
            <div class="field-block mb-3">
                <label class="field-label">Notes</label>
                <textarea
                    class="form-control"
                    placeholder="Add notes about this shift..."
                    [(ngModel)]="notes"
                    rows="2"></textarea>
            </div>
        </div>
    </ng-container>

    <ng-container modal-actions>
        <button mat-button (click)="onCancel()" [disabled]="submitting">Cancel</button>
        <button mat-raised-button color="primary" (click)="onSubmit()" [disabled]="!canSubmit">
            @if (submitting) {
                <i class="fa-solid fa-spinner fa-spin me-1"></i>
            }
            <i class="fa-solid fa-plus me-1"></i>
            {{ submitLabel }}
        </button>
    </ng-container>

</app-oee-modal>
